import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import {
  ArrowLeft,
  Users,
  MapPin,
  Calendar,
  Clock,
  User,
  CheckCircle,
  XCircle,
  Plus,
} from 'lucide-react';
import { intervisieApi } from '../services/intervisieApi';
import { IntervisieSessieRij, IntervisieTeamsPlanKaart } from '../components/IntervisieTeamsSessies';
import { toast } from 'react-hot-toast';

const STATUS_CLASSES: Record<string, string> = {
  beschikbaar: 'bg-green-100 text-green-700',
  bijna_vol: 'bg-yellow-100 text-yellow-700',
  vol: 'bg-red-100 text-red-700',
};

export const IntervisieGroepDetailPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: group, isLoading } = useQuery({
    queryKey: ['intervisie-group', id],
    queryFn: () => intervisieApi.getGroup(id!),
    enabled: !!id,
  });

  const joinMutation = useMutation({
    mutationFn: () => intervisieApi.joinGroup(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intervisie-group', id] });
      queryClient.invalidateQueries({ queryKey: ['intervisie-groups'] });
      toast.success(t('intervisie.joinSuccess'));
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || t('intervisie.error');
      toast.error(msg);
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-100 rounded w-full" />
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <p className="text-gray-500 text-center py-8">{t('intervisie.groupNotFound')}</p>
        </Card>
      </div>
    );
  }

  const statusClass = STATUS_CLASSES[group.status] || STATUS_CLASSES.beschikbaar;

  const STATUS_LABELS: Record<string, string> = {
    beschikbaar: t('intervisie.statusAvailable'),
    bijna_vol: t('intervisie.statusAlmostFull'),
    vol: t('intervisie.statusFull'),
  };
  const statusLabel = STATUS_LABELS[group.status] || group.status;

  const isMember = !!group.user_membership;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/intervisie" className="hover:text-[#0B1F3A] flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> {t('intervisie.title')}
        </Link>
        <span>›</span>
        <span className="text-gray-700">{group.name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-mono text-gray-400">#{group.group_number}</span>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${statusClass}`}>
                    {statusLabel}
                  </span>
                </div>
                <h1 className="text-xl font-bold text-gray-900">{group.name}</h1>
              </div>
              <div className="flex-shrink-0">
                {isMember ? (
                  <div className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                    <CheckCircle className="w-4 h-4" />
                    {t('intervisie.registered')}
                  </div>
                ) : group.status === 'vol' ? (
                  <div className="flex items-center gap-1.5 text-sm text-red-500">
                    <XCircle className="w-4 h-4" />
                    {t('intervisie.statusFull')}
                  </div>
                ) : (
                  <Button onClick={() => joinMutation.mutate()} loading={joinMutation.isPending}>
                    {t('intervisie.register')}
                  </Button>
                )}
              </div>
            </div>

            {group.description && (
              <p className="text-sm text-gray-600 mt-4 leading-relaxed">{group.description}</p>
            )}

            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              {group.planning && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {group.planning}
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-4 h-4 text-gray-400" />
                {group.member_count} / {group.max_members} {t('intervisie.participants')}
              </div>
              {(group.plaats || group.is_online) && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {group.is_online ? t('intervisie.online') : group.plaats}
                  {group.provincie && !group.is_online && ` (${group.provincie})`}
                </div>
              )}
            </div>
          </Card>

          {/* Members */}
          <Card>
            <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#0B1F3A]" />
              {t('intervisie.participants')} ({group.member_count})
            </h2>
            {group.members.length === 0 ? (
              <p className="text-sm text-gray-400">{t('intervisie.noParticipants')}</p>
            ) : (
              <div className="space-y-2">
                {group.members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#0B1F3A] flex items-center justify-center text-white text-xs flex-shrink-0">
                      {m.profile_photo_path ? (
                        <img
                          src={m.profile_photo_path}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-800">
                        {m.first_name} {m.last_name}
                      </span>
                      {m.role === 'moderator' && (
                        <span className="ml-2 text-xs text-[#0B1F3A] bg-blue-50 px-1.5 py-0.5 rounded">
                          {t('intervisie.contactPerson')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Sessions */}
          {group.sessions.length > 0 && (
            <Card>
              <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#0B1F3A]" />
                {t('intervisie.sessions')} ({group.sessions.length})
              </h2>
              <div className="space-y-2">
                {group.sessions.map((s) => (
                  <IntervisieSessieRij key={s.id} groupId={id!} sessie={s} isContactPerson={group.is_contact_person} members={group.members} />
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <h3 className="font-semibold text-gray-900 mb-3">{t('intervisie.contactPerson')}</h3>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0B1F3A] flex items-center justify-center text-white">
                <User className="w-4 h-4" />
              </div>
              <span className="text-sm text-gray-700">
                {group.contact_first_name} {group.contact_last_name}
              </span>
            </div>
          </Card>

          {group.is_contact_person && (
            <Card>
              <h3 className="font-semibold text-gray-900 mb-3">{t('intervisie.manage')}</h3>
              <Button
                className="w-full"
                size="sm"
                onClick={() => navigate(`/intervisie/${id}/sessie-registreren`)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('intervisie.registerSession')}
              </Button>
              <IntervisieTeamsPlanKaart groupId={id!} beschikbaar={!!group.teams_beschikbaar} />
            </Card>
          )}

          <Card>
            <h3 className="font-semibold text-gray-900 mb-3">{t('intervisie.aboutIntervisie')}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {t('intervisie.aboutIntervisieText')}
            </p>
            <div className="mt-3 text-sm text-gray-600 space-y-1">
              <div>{t('intervisie.minMax')}</div>
              <div>{t('intervisie.facilitatorNote')}</div>
              <div>{t('intervisie.methodologyNote')}</div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
